import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ovrApi, { clearOvrToken, getOvrToken } from '@/lib/ownerOverride';
import { errMsg } from '@/lib/api';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PERM_GROUPS, ALL_PERM_KEYS } from '@/lib/permissions';
import {
  ShieldCheck, Search, User, LogOut, Clock, KeyRound, Fingerprint, Smartphone,
  Trash2, Loader2, RefreshCw, Check, Copy, Pencil, ArrowLeft,
} from 'lucide-react';

const SESSION_SECONDS = 10 * 60;
const fmtClock = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
const fmtDate = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }); } catch (e) { return '—'; } };

export default function OwnerOverride() {
  const navigate = useNavigate();
  const [ownerName, setOwnerName] = useState('');
  const [stores, setStores] = useState([]);
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // full user obj
  const [remaining, setRemaining] = useState(SESSION_SECONDS);
  const lastActivity = useRef(Date.now());

  const endSession = useCallback((silent, reason) => {
    clearOvrToken();
    if (!silent) toast[reason === 'expired' ? 'error' : 'success'](reason === 'expired' ? 'Owner Access expired.' : 'Owner Mode ended.');
    navigate('/login', { replace: true });
  }, [navigate]);

  // Any override call funnels through here so activity resets the timer and a
  // 401/403 (expiry/exit) cleanly returns to the login screen.
  const call = useCallback(async (fn) => {
    try {
      const r = await fn();
      lastActivity.current = Date.now();
      setRemaining(SESSION_SECONDS);
      return r;
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401 || code === 403) { endSession(false, 'expired'); throw e; }
      throw e;
    }
  }, [endSession]);

  const loadUsers = useCallback(async (q) => {
    const r = await call(() => ovrApi.get('/owner-override/users', { params: { query: q || '' } }));
    setUsers(r.data.data || []);
  }, [call]);

  useEffect(() => {
    if (!getOvrToken()) { navigate('/login', { replace: true }); return; }
    (async () => {
      try {
        const b = await call(() => ovrApi.get('/owner-override/bootstrap'));
        setStores(b.data.data.stores || []);
        setOwnerName(b.data.data.owner?.name || b.data.data.owner?.username || 'Owner');
        await loadUsers('');
      } catch (e) { console.debug('[owner-override] bootstrap load failed (auth errors handled in call)', e); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inactivity countdown — auto-exits after 10 minutes of no actions.
  useEffect(() => {
    const iv = setInterval(() => {
      const left = SESSION_SECONDS - Math.floor((Date.now() - lastActivity.current) / 1000);
      setRemaining(left);
      if (left <= 0) { clearInterval(iv); endSession(false, 'expired'); }
    }, 1000);
    return () => clearInterval(iv);
  }, [endSession]);

  const exit = async () => {
    try { await ovrApi.post('/owner-override/exit'); } catch (e) { console.debug('[owner-override] exit call failed (ignored, ending locally)', e); }
    endSession(false, 'exit');
  };

  const openUser = async (u) => {
    try {
      const r = await call(() => ovrApi.get(`/owner-override/users/${u.id}`));
      setSelected(r.data.data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="app-bg min-h-screen" data-testid="owner-override-screen"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
      {/* Header */}
      <div className="sticky top-0 z-30 border-b border-border bg-card/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-2 px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary"><ShieldCheck className="h-5 w-5" /></span>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">Owner Mode</p>
              <p className="truncate text-xs text-muted-foreground">{ownerName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${remaining <= 60 ? 'bg-destructive/10 text-destructive' : 'bg-secondary text-muted-foreground'}`} data-testid="owner-override-timer">
              <Clock className="h-3.5 w-3.5" />{fmtClock(Math.max(0, remaining))}
            </span>
            <Button variant="destructive" size="sm" className="h-9 rounded-xl" onClick={exit} data-testid="owner-override-exit">
              <LogOut className="mr-1 h-4 w-4" />Exit
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-4 py-4">
        {!selected ? (
          <>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input value={query} onChange={(e) => { setQuery(e.target.value); loadUsers(e.target.value); }}
                placeholder="Search user by name or username" className="h-11 rounded-xl pl-9" data-testid="owner-override-search" />
            </div>
            {loading ? (
              <div className="flex items-center gap-2 py-10 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>
            ) : users.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground" data-testid="owner-override-empty">No users found.</p>
            ) : (
              <div className="space-y-2">
                {users.map((u) => (
                  <button key={u.id} onClick={() => openUser(u)} data-testid={`owner-override-user-${u.username}`}
                    className="flex w-full items-center justify-between gap-2 rounded-xl border border-border bg-card p-3 text-left transition-colors hover:border-primary/40">
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary"><User className="h-4 w-4" /></span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{u.name} <span className="text-xs text-muted-foreground">({u.username})</span></p>
                        <p className="truncate text-xs text-muted-foreground">{u.owner_access ? 'OWNER ACCESS' : u.role}{u.active ? '' : ' · disabled'}</p>
                      </div>
                    </div>
                    <span className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium">Manage</span>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <ManageUser key={selected.id} user={selected} stores={stores} call={call}
            onBack={() => setSelected(null)} onRefresh={(u) => { setSelected(u); loadUsers(query); }} />
        )}
      </div>
    </div>
  );
}

function ManageUser({ user, stores, call, onBack, onRefresh }) {
  const [name, setName] = useState(user.name || '');
  const [role, setRole] = useState(user.role || 'STAFF');
  const [storeId, setStoreId] = useState(user.store_id || '');
  const [active, setActive] = useState(user.active !== false);
  const [ownerAccess, setOwnerAccess] = useState(!!user.owner_access);
  const [allowBio, setAllowBio] = useState(!!user.allow_biometric);
  const [perms, setPerms] = useState(Array.isArray(user.permissions) ? user.permissions : []);
  const [saving, setSaving] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [devices, setDevices] = useState([]);
  const [revokeDev, setRevokeDev] = useState(null);
  const [confirmOwner, setConfirmOwner] = useState(false);

  const loadDevices = useCallback(async () => {
    try { const r = await call(() => ovrApi.get(`/owner-override/users/${user.id}/webauthn/credentials`)); setDevices(r.data.data.credentials || []); }
    catch (e) { console.debug('[owner-override] device list load failed', e); }
  }, [call, user.id]);
  useEffect(() => { loadDevices(); }, [loadDevices]);

  const togglePerm = (k) => setPerms((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);

  const doSave = async (highRiskConfirmed) => {
    const promoting = (ownerAccess && !user.owner_access) || (role === 'OWNER' && user.role !== 'OWNER');
    if (promoting && !highRiskConfirmed) { setConfirmOwner(true); return; }
    setSaving(true);
    try {
      const body = { name, role, store_id: storeId || null, active, owner_access: ownerAccess,
        allow_biometric: allowBio, permissions: perms };
      if (promoting) body.confirm_high_risk = true;
      const r = await call(() => ovrApi.patch(`/owner-override/users/${user.id}`, body));
      toast.success('Changes saved.');
      onRefresh(r.data.data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); setConfirmOwner(false); }
  };

  const revokeDevice = async () => {
    try { await call(() => ovrApi.post(`/owner-override/webauthn/credentials/${revokeDev.id}/revoke`)); toast.success('Device removed.'); setRevokeDev(null); loadDevices(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="owner-override-manage">
      <button onClick={onBack} className="mb-3 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="owner-override-back">
        <ArrowLeft className="h-4 w-4" />All users
      </button>

      {/* ACCOUNT */}
      <section className="mb-3 rounded-2xl border border-border bg-card p-4">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Account</p>
        <div className="mb-3">
          <Label className="text-xs">Full name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="ovr-name" />
        </div>
        <div className="mb-3 flex items-end justify-between gap-2">
          <div className="min-w-0">
            <Label className="text-xs">Username</Label>
            <p className="mt-1 truncate font-mono text-sm">{user.username}</p>
          </div>
          <Button variant="secondary" className="h-10 rounded-xl" onClick={() => setRenameOpen(true)} data-testid="ovr-rename-open"><Pencil className="mr-1.5 h-4 w-4" />Change</Button>
        </div>
        <div className="flex items-center justify-between rounded-xl border border-border p-3">
          <div><p className="text-sm font-medium">Account status</p><p className="text-xs text-muted-foreground">{active ? 'Active' : 'Disabled'}</p></div>
          <Switch checked={active} onCheckedChange={setActive} data-testid="ovr-active" />
        </div>
      </section>

      {/* ACCESS */}
      <section className="mb-3 rounded-2xl border border-border bg-card p-4">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Access</p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="ovr-role"><SelectValue /></SelectTrigger>
              <SelectContent>{['STAFF', 'MANAGER', 'OWNER', 'PARTNER'].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Store</Label>
            <Select value={storeId || 'none'} onValueChange={(v) => setStoreId(v === 'none' ? '' : v)}>
              <SelectTrigger className="mt-1 h-11 rounded-xl" data-testid="ovr-store"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent><SelectItem value="none">All stores</SelectItem>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between rounded-xl border border-destructive/30 bg-destructive/5 p-3">
          <div><p className="text-sm font-semibold text-destructive">Owner Access</p><p className="text-xs text-muted-foreground">Full Owner-level access.</p></div>
          <Switch checked={ownerAccess} onCheckedChange={setOwnerAccess} data-testid="ovr-owner-access" />
        </div>

        {!ownerAccess && role !== 'PARTNER' && (
          <div className="mt-3 rounded-xl border border-border p-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-sm font-semibold">Permissions</p>
              <div className="flex gap-2">
                <button onClick={() => setPerms([...ALL_PERM_KEYS])} className="rounded-lg border border-border px-2 py-1 text-xs" data-testid="ovr-perm-all">All</button>
                <button onClick={() => setPerms([])} className="rounded-lg border border-border px-2 py-1 text-xs" data-testid="ovr-perm-none">None</button>
              </div>
            </div>
            <div className="space-y-3">
              {PERM_GROUPS.map((g) => (
                <div key={g.title}>
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{g.title}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {g.perms.map(([key, label]) => (
                      <label key={key} className="flex items-center gap-2 rounded-lg border border-border px-2.5 py-2 text-sm" data-testid={`ovr-perm-${key}`}>
                        <Checkbox checked={perms.includes(key)} onCheckedChange={() => togglePerm(key)} />
                        <span className="truncate">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* SECURITY */}
      <section className="mb-3 rounded-2xl border border-border bg-card p-4">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Security</p>
        <div className="mb-3 flex items-center justify-between rounded-xl border border-border p-3">
          <div className="flex items-center gap-2"><KeyRound className="h-4 w-4" /><div><p className="text-sm font-medium">Password</p><p className="text-xs text-muted-foreground">Reset without the old password</p></div></div>
          <Button variant="secondary" className="h-10 rounded-xl" onClick={() => setResetOpen(true)} data-testid="ovr-reset-open"><RefreshCw className="mr-1.5 h-4 w-4" />Reset</Button>
        </div>
        <div className="mb-3 flex items-center justify-between rounded-xl border border-border bg-secondary/30 p-3">
          <div className="flex items-start gap-2"><Fingerprint className="mt-0.5 h-4 w-4 text-primary" /><div><p className="text-sm font-medium">Biometric Quick Login</p><p className="text-xs text-muted-foreground">Turning OFF revokes devices.</p></div></div>
          <Switch checked={allowBio} onCheckedChange={setAllowBio} data-testid="ovr-allow-biometric" />
        </div>
        <div className="rounded-xl border border-border p-3">
          <p className="mb-2 text-sm font-medium">Registered devices</p>
          {devices.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="ovr-no-devices">No devices registered.</p>
          ) : devices.map((c) => (
            <div key={c.id} className="flex items-center justify-between py-1.5" data-testid={`ovr-device-${c.id}`}>
              <div className="flex min-w-0 items-center gap-2"><Smartphone className="h-4 w-4 text-muted-foreground" /><span className="truncate text-sm">{c.label || 'Mobile device'} <span className="text-xs text-muted-foreground">· {fmtDate(c.created_at)}</span></span></div>
              <button onClick={() => setRevokeDev(c)} className="flex h-8 w-8 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10" data-testid={`ovr-device-revoke-${c.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </section>

      <Button className="h-12 w-full rounded-xl text-base font-semibold" onClick={() => doSave(false)} disabled={saving} data-testid="ovr-save">
        {saving ? <span className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" />Saving…</span> : 'Save Changes'}
      </Button>

      <RenameDialog open={renameOpen} user={user} call={call} onClose={() => setRenameOpen(false)} onDone={(u) => { setRenameOpen(false); onRefresh(u); }} />
      <OvrResetDialog open={resetOpen} user={user} call={call} onClose={() => setResetOpen(false)} />

      <AlertDialog open={confirmOwner} onOpenChange={(o) => { if (!o) setConfirmOwner(false); }}>
        <AlertDialogContent data-testid="ovr-highrisk-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Grant Owner access to this user?</AlertDialogTitle>
            <AlertDialogDescription>This gives <b>{user.name || user.username}</b> complete Owner-level access to the entire business. Continue?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => doSave(true)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="ovr-highrisk-confirm">Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!revokeDev} onOpenChange={(o) => { if (!o) setRevokeDev(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove this device?</AlertDialogTitle>
            <AlertDialogDescription>It will no longer be able to use Quick Login.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={revokeDevice} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="ovr-device-revoke-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RenameDialog({ open, user, call, onClose, onDone }) {
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setVal(''); }, [open]);
  const submit = async () => {
    const v = val.trim().toLowerCase();
    if (v.length < 2) { toast.error('Enter a valid username.'); return; }
    setBusy(true);
    try { const r = await call(() => ovrApi.post(`/owner-override/users/${user.id}/rename-username`, { new_username: v })); toast.success('Username changed. The user keeps all history.'); onDone(r.data.data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="ovr-rename-dialog">
        <DialogHeader><DialogTitle>Change username</DialogTitle>
          <DialogDescription>Rename <b>{user.username}</b>. Sales history and incentives stay attached to the same person.</DialogDescription></DialogHeader>
        <div className="py-1">
          <Label className="text-xs">New username</Label>
          <Input value={val} onChange={(e) => setVal(e.target.value)} autoCapitalize="none" className="mt-1 h-11 rounded-xl" data-testid="ovr-rename-input" />
        </div>
        <DialogFooter>
          <Button variant="ghost" className="h-11 rounded-xl" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="h-11 rounded-xl" onClick={submit} disabled={busy} data-testid="ovr-rename-submit">{busy ? 'Saving…' : 'Change username'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OvrResetDialog({ open, user, call, onClose }) {
  const [mode, setMode] = useState('temp');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [requireChange, setRequireChange] = useState(true);
  const [revokeSessions, setRevokeSessions] = useState(true);
  const [revokeDevices, setRevokeDevices] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { if (open) { setMode('temp'); setNewPw(''); setConfirmPw(''); setRequireChange(true); setRevokeSessions(true); setRevokeDevices(false); setBusy(false); setResult(null); setCopied(false); } }, [open]);

  const submit = async () => {
    if (mode === 'manual') {
      if (newPw.length < 4) { toast.error('New password must be at least 4 characters.'); return; }
      if (newPw !== confirmPw) { toast.error('Passwords do not match.'); return; }
    }
    setBusy(true);
    try {
      const body = { generate_temp: mode === 'temp', require_change: requireChange, revoke_sessions: revokeSessions, revoke_quicklogin: revokeDevices };
      if (mode === 'manual') body.new_password = newPw;
      const r = await call(() => ovrApi.post(`/owner-override/users/${user.id}/reset-password`, body));
      if (r.data.data.temporary_password) setResult(r.data.data);
      else { toast.success('Password reset.'); onClose(); }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const copy = async () => { try { await navigator.clipboard.writeText(result.temporary_password); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (e) { console.debug('clipboard copy failed', e); toast.error('Could not copy — please copy it manually.'); } };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="ovr-reset-dialog">
        {!result ? (
          <>
            <DialogHeader><DialogTitle>Reset password</DialogTitle>
              <DialogDescription>For <b>{user.name || user.username}</b>. The old password is not required.</DialogDescription></DialogHeader>
            <div className="space-y-3 py-1">
              <div className="flex gap-2">
                <button onClick={() => setMode('temp')} className={`flex-1 rounded-xl border px-3 py-2 text-sm font-medium ${mode === 'temp' ? 'border-primary bg-primary text-primary-foreground' : 'border-border'}`} data-testid="ovr-reset-temp">Temporary</button>
                <button onClick={() => setMode('manual')} className={`flex-1 rounded-xl border px-3 py-2 text-sm font-medium ${mode === 'manual' ? 'border-primary bg-primary text-primary-foreground' : 'border-border'}`} data-testid="ovr-reset-manual">Set password</button>
              </div>
              {mode === 'temp' ? (
                <p className="rounded-lg bg-secondary/40 p-2.5 text-xs text-muted-foreground">A secure temporary password (SHBZ-XXXXXX) will be generated and shown once.</p>
              ) : (
                <div className="space-y-2">
                  <div><Label className="text-xs">New password</Label><Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="ovr-reset-new" /></div>
                  <div><Label className="text-xs">Confirm</Label><Input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="ovr-reset-confirm" /></div>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={requireChange} onCheckedChange={(v) => setRequireChange(!!v)} data-testid="ovr-reset-require" /><span>Require change at next login</span></label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={revokeSessions} onCheckedChange={(v) => setRevokeSessions(!!v)} data-testid="ovr-reset-sessions" /><span>Sign out existing sessions</span></label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={revokeDevices} onCheckedChange={(v) => setRevokeDevices(!!v)} data-testid="ovr-reset-devices" /><span>Also revoke Quick Login devices</span></label>
            </div>
            <DialogFooter>
              <Button variant="ghost" className="h-11 rounded-xl" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button className="h-11 rounded-xl" onClick={submit} disabled={busy} data-testid="ovr-reset-submit">{busy ? 'Resetting…' : 'Reset Password'}</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader><DialogTitle>Password reset</DialogTitle><DialogDescription>Share this with {user.name || user.username}. It won't be shown again.</DialogDescription></DialogHeader>
            <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-secondary/40 p-3">
              <span className="font-mono text-lg font-semibold tracking-wider" data-testid="ovr-reset-temp-value">{result.temporary_password}</span>
              <Button variant="secondary" className="h-9 rounded-lg" onClick={copy} data-testid="ovr-reset-copy">{copied ? <><Check className="mr-1 h-4 w-4" />Copied</> : <><Copy className="mr-1 h-4 w-4" />Copy</>}</Button>
            </div>
            <DialogFooter><Button className="h-11 w-full rounded-xl" onClick={onClose} data-testid="ovr-reset-done">Done</Button></DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
